/**
 * CheckoutModal Component
 * Modal for initiating Stripe Checkout
 */

import { useState } from 'react'
import { backendApiService } from '@talktoast/shared/services/backendApiService'
import { BillingInterval } from '@talktoast/shared/types/subscription'

interface CheckoutModalProps {
  isOpen: boolean
  onClose: () => void
  tier: 'premium'
  defaultInterval?: BillingInterval
}

export function CheckoutModal({
  isOpen,
  onClose,
  tier,
  defaultInterval = 'yearly'
}: CheckoutModalProps) {
  const [interval, setInterval] = useState<BillingInterval>(defaultInterval)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const config = {
    premium: {
      name: 'Premium',
      monthlyPrice: 4.99,
      yearlyPrice: 49.99
    }
  }

  const { name, monthlyPrice, yearlyPrice } = config[tier] ?? config.premium
  const price = interval === 'monthly' ? monthlyPrice : yearlyPrice
  const pricePerMonth = interval === 'yearly' ? (yearlyPrice / 12).toFixed(2) : monthlyPrice.toFixed(2)
  const savings = interval === 'yearly' ? Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100) : 0

  const handleCheckout = async () => {
    try {
      setLoading(true)
      setError(null)

      // Create Stripe Checkout session
      const { url } = await backendApiService.createStripeCheckout(tier, interval)

      // Redirect to Stripe Checkout
      window.location.href = url
    } catch (err: any) {
      console.error('[CheckoutModal] Checkout failed:', err)
      const isAuthError = /token expired|session expired|unauthorized|invalid.*token/i.test(err.message || '')
      setError(
        isAuthError
          ? 'Your session has expired. Please sign in again to continue.'
          : err.message || 'Failed to start checkout. Please try again.'
      )
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            Upgrade to {name}
          </h3>
          <p className="text-gray-600">Start your 7-day free trial</p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <button
            onClick={() => setInterval('monthly')}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
              interval === 'monthly'
                ? 'bg-avocado-green text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval('yearly')}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 relative ${
              interval === 'yearly'
                ? 'bg-avocado-green text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Yearly
            {savings > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                -{savings}%
              </span>
            )}
          </button>
        </div>

        {/* Pricing */}
        <div className="text-center mb-6 p-4 bg-gray-50 rounded-xl">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-4xl font-bold text-gray-900">${pricePerMonth}</span>
            <span className="text-gray-600">/mo</span>
          </div>
          {interval === 'yearly' && (
            <p className="text-sm text-gray-500 mt-2">
              ${yearlyPrice.toFixed(2)} billed annually
            </p>
          )}
          <p className="text-xs text-avocado-green font-semibold mt-2">
            First 7 days free, then ${price.toFixed(2)}/{interval === 'monthly' ? 'month' : 'year'}
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Checkout button */}
        <button
          onClick={handleCheckout}
          disabled={loading}
          className="w-full bg-avocado-green text-white py-3 px-4 rounded-lg font-semibold hover:bg-avocado-green-dark transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading checkout...</span>
            </>
          ) : (
            <>
              <span>Continue to Checkout</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </>
          )}
        </button>

        {/* Security note */}
        <p className="text-xs text-center text-gray-500 mt-4">
          🔒 Secure payment powered by Stripe
        </p>
      </div>
    </div>
  )
}
